import { useRef, useState } from 'react';

type PlayableSegment = {
  /** Unique across recordings — a segment id alone is only unique within one. */
  key: string;
  fileId: string;
  startSeconds: number;
  endSeconds: number;
};

type SegmentPlayback = {
  /** Attach to each recording's mounted `<audio>`; playback is driven off them, not `new Audio()`. */
  registerAudio: (fileId: string) => (element: HTMLAudioElement | null) => void;
  isPlaying: (key: string) => boolean;
  /** Playback position within the given segment, 0..1 (0 when it's not the active segment). */
  progressFor: (key: string) => number;
  toggle: (segment: PlayableSegment) => void;
  handleTimeUpdate: () => void;
  handleEnded: () => void;
  handleError: () => void;
};

// A short tail past the cited end so auto-pause doesn't clip the last word when the
// transcription's end marker lands slightly early.
const AUTO_PAUSE_TAIL_SECONDS = 0.3;

/**
 * Plays a segment's [start, end] range on its own recording's mounted `<audio>` element: seeks to
 * the start, pauses just past the end, and reports progress. A document can be written from
 * several meetings, so each recording mounts its own element and a segment says which it belongs
 * to. Only one segment plays at a time; toggling the active segment pauses it.
 */
export function useSegmentPlayback(): SegmentPlayback {
  const audiosRef = useRef(new Map<string, HTMLAudioElement>());
  const activeSegmentRef = useRef<PlayableSegment | null>(null);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);

  const registerAudio = (fileId: string) => (element: HTMLAudioElement | null) => {
    if (element) audiosRef.current.set(fileId, element);
    else audiosRef.current.delete(fileId);
  };

  const activeAudio = () => {
    const segment = activeSegmentRef.current;
    return segment ? (audiosRef.current.get(segment.fileId) ?? null) : null;
  };

  const stop = () => {
    activeSegmentRef.current = null;
    setActiveKey(null);
    setProgress(0);
  };

  const handleTimeUpdate = () => {
    const audio = activeAudio();
    const segment = activeSegmentRef.current;
    if (!audio || !segment) return;
    if (audio.currentTime >= segment.endSeconds + AUTO_PAUSE_TAIL_SECONDS) {
      audio.pause();
      stop();
      return;
    }
    const span = segment.endSeconds - segment.startSeconds;
    const elapsed = audio.currentTime - segment.startSeconds;
    setProgress(span > 0 ? Math.min(1, Math.max(0, elapsed / span)) : 0);
  };

  const handleEnded = () => stop();

  const handleError = () => {
    activeAudio()?.pause();
    stop();
  };

  const toggle = (segment: PlayableSegment) => {
    const audio = audiosRef.current.get(segment.fileId);
    if (!audio) return;

    // Decide on the ref, which is always current — `activeKey` state can lag a just-committed reset.
    if (activeSegmentRef.current?.key === segment.key) {
      audio.pause();
      stop();
      return;
    }

    // Another recording may be mid-play; only one segment sounds at a time.
    activeAudio()?.pause();
    audio.pause();
    activeSegmentRef.current = segment;
    audio.currentTime = segment.startSeconds;
    setActiveKey(segment.key);
    setProgress(0);
    void audio.play().catch(() => {
      // Only reset if this segment is still the active one — a later toggle may have superseded it.
      if (activeSegmentRef.current?.key === segment.key) stop();
    });
  };

  return {
    registerAudio,
    isPlaying: (key: string) => activeKey === key,
    progressFor: (key: string) => (activeKey === key ? progress : 0),
    toggle,
    handleTimeUpdate,
    handleEnded,
    handleError,
  };
}
